import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Services
import { getManagerLayout, postMenu } from './layoutService';

// Domain
import { buildMenuPostRequest } from '../../domain/menu/menuManagementUseCase';

// Components
import useToast from '../../components/molecules/Toast/useToast';

export const LAYOUT_QUERY_KEY = 'layout';
export const MENU_MUTATION_KEY = 'menu';

/**
 * Get manager menu items
 * @param {{path: string, contexte?: string | null}} [params = {}] - The query params
 * @param {QueriesOptions} [options = {}] - The query options
 * @returns {import('@tanstack/react-query').UseQueryResult<ApiManagerLayout, Error>} The manager menu items
 */
export const useManagerLayout = (params = { path: '' }, options = {}) => {
  const { path, contexte = null } = params;

  return useQuery({
    queryKey: [LAYOUT_QUERY_KEY, path, contexte],
    queryFn: () => getManagerLayout(path, contexte),
    ...options,
  });
};

/**
 * @typedef {object} MenuSaveVariables
 * @property {ApiManagerMenuItem[]} nextItems - Full menu tree to save
 * @property {ApiManagerMenuItem[] | null} [bottomSectionItems] - Full bottom section tree to save
 * @property {boolean} [optimistic=false] - Patch the cached menus before the POST (rollback on error)
 */

/**
 * Replace the menu in every cached layout.
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {(menu: ApiManagerMenu) => ApiManagerMenu} updateMenu
 */
const patchAllLayoutMenus = (queryClient, updateMenu) => {
  queryClient.setQueriesData(
    { queryKey: [LAYOUT_QUERY_KEY] },
    (/** @type {ApiManagerLayout | undefined} */ old) => (old?.menu ? { ...old, menu: updateMenu(old.menu) } : old),
  );
};

/**
 * Post menu configuration. The menu is global: the response replaces `menu` in every cached layout.
 * @param {import('@tanstack/react-query').UseMutationOptions<ApiMenuPostResponse, Error, MenuSaveVariables, unknown>} [options = {}]
 * @returns {import('@tanstack/react-query').UseMutationResult<ApiMenuPostResponse, Error, MenuSaveVariables, unknown>}
 */
export const useMenuMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();
  const { onSuccess, onError, ...restOptions } = options;

  return useMutation({
    mutationKey: [MENU_MUTATION_KEY],
    // Saves send the full tree: run them one after another so the server keeps the last state
    scope: { id: MENU_MUTATION_KEY },
    mutationFn: ({ nextItems, bottomSectionItems }) => postMenu(buildMenuPostRequest(nextItems, bottomSectionItems)),
    ...restOptions,
    onMutate: async ({ nextItems, bottomSectionItems, optimistic }) => {
      if (!optimistic) return {};
      await queryClient.cancelQueries({ queryKey: [LAYOUT_QUERY_KEY] });
      const previous = queryClient.getQueriesData({ queryKey: [LAYOUT_QUERY_KEY] });
      patchAllLayoutMenus(queryClient, (menu) => ({
        ...menu,
        items: nextItems,
        bottomSection: bottomSectionItems ? { ...menu.bottomSection, items: bottomSectionItems } : menu.bottomSection,
      }));
      return { previous };
    },
    onSuccess: (data, variables, context) => {
      patchAllLayoutMenus(queryClient, () => data.menu);
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      const previous = /** @type {{ previous?: Array<[import('@tanstack/react-query').QueryKey, unknown]> } | undefined} */ (
        context
      )?.previous;
      previous?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      notify({
        message: error?.message || t('toast.general.error'),
        type: 'alert',
      });
      onError?.(error, variables, context);
    },
  });
};
